import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { profileService, StudentProfile as StudentProfileType } from '../../services/profileService';
import ProfileViewsInsights from '../ProfileViewsInsights';
import {
  UserIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  ChartBarIcon,
  TrophyIcon,
  PencilIcon,
  StarIcon,
  CalendarIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  BookOpenIcon,
  DocumentCheckIcon,
  XMarkIcon,
  PlusIcon,
  EyeIcon
} from '@heroicons/react/24/outline';



const StudentProfile: React.FC = () => {
  const { userProfile } = useAuth();
  const [profile, setProfile] = useState<StudentProfileType | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const profileData = await profileService.getCurrentProfile() as StudentProfileType;
      const formattedProfile = profileService.formatProfileForDisplay(profileData);
      
      // Ensure displayName is properly set from user data if missing or incomplete
      if (userProfile && (!formattedProfile.displayName || formattedProfile.displayName.trim() === '' || formattedProfile.displayName === userProfile.firstName)) {
        const fullName = `${userProfile.firstName} ${userProfile.lastName}`.trim();
        if (fullName && fullName !== ' ') {
          formattedProfile.displayName = fullName;
        }
      }
      
      setProfile(formattedProfile);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;

    try {
      setLoading(true);
      
      // Ensure displayName is properly set before saving
      const profileToSave = { ...profile };
      if (userProfile && (!profileToSave.displayName || profileToSave.displayName.trim() === '')) {
        const fullName = `${userProfile.firstName} ${userProfile.lastName}`.trim();
        if (fullName && fullName !== ' ') {
          profileToSave.displayName = fullName;
        }
      }
      
      // Validate profile data
      const errors = profileService.validateStudentProfile(profileToSave);
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }

      const updatedProfile = await profileService.updateProfile(profileToSave) as StudentProfileType;
      const formattedProfile = profileService.formatProfileForDisplay(updatedProfile);
      
      // Ensure displayName is still properly set after update
      if (userProfile && (!formattedProfile.displayName || formattedProfile.displayName.trim() === '')) {
        const fullName = `${userProfile.firstName} ${userProfile.lastName}`.trim();
        if (fullName && fullName !== ' ') {
          formattedProfile.displayName = fullName;
        }
      }
      
      setProfile(formattedProfile);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = (field: keyof StudentProfileType, value: any) => {
    if (!profile) return;
    setProfile(prev => prev ? { ...prev, [field]: value } : null);
  };


  const tabs = [
    { id: 'overview', name: 'Overview', icon: UserIcon },
    { id: 'education', name: 'Education', icon: AcademicCapIcon },
    { id: 'experience', name: 'Experience', icon: BriefcaseIcon },
    { id: 'skills', name: 'Skills & Ratings', icon: ChartBarIcon },
    { id: 'insights', name: 'Profile Insights', icon: EyeIcon }
  ];

  const renderOverviewTab = () => {
    if (!profile) return null;
    
    return (
      <div className="space-y-6">
        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-20 w-20 bg-indigo-100 rounded-full flex items-center justify-center">
                {profile.avatar ? (
                  <img src={profile.avatar} alt="" className="h-20 w-20 rounded-full" />
                ) : (
                  <UserIcon className="h-12 w-12 text-indigo-600" />
                )}
              </div>
              <div>
                {isEditing ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={profile.displayName}
                      onChange={(e) => updateProfile('displayName', e.target.value)}
                      className="text-2xl font-bold text-gray-900 border-b border-gray-300 focus:border-indigo-500 outline-none bg-transparent"
                      placeholder="Display Name"
                    />
                    <textarea
                      value={profile.bio || ''}
                      onChange={(e) => updateProfile('bio', e.target.value)}
                      className="text-gray-600 border border-gray-300 rounded px-3 py-2 w-full focus:border-indigo-500 outline-none resize-none"
                      placeholder="Bio"
                      rows={2}
                    />
                  </div>
                ) : (
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{profile.displayName}</h1>
                    <p className="text-gray-600">{profile.bio}</p>
                  </div>
                )}
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  <div className="flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    {isEditing ? (
                      <input
                        type="text"
                        value={profile.location || ''}
                        onChange={(e) => updateProfile('location', e.target.value)}
                        className="border-b border-gray-300 focus:border-indigo-500 outline-none bg-transparent text-sm"
                        placeholder="Location"
                      />
                    ) : (
                      profile.location
                    )}
                  </div>
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-4 w-4 mr-1" />
                    {isEditing ? (
                      <input
                        type="email"
                        value={profile.email}
                        onChange={(e) => updateProfile('email', e.target.value)}
                        className="border-b border-gray-300 focus:border-indigo-500 outline-none bg-transparent text-sm"
                        placeholder="Email"
                      />
                    ) : (
                      profile.email
                    )}
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center px-4 py-2 text-indigo-600 hover:text-indigo-800"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit Profile
            </button>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <BookOpenIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Courses Completed</p>
              <p className="text-2xl font-bold text-gray-900">{profile.courseStats.completed}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Average Score</p>
              <p className="text-2xl font-bold text-gray-900">{Math.round(profile.courseStats.averageScore)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <BriefcaseIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Job Applications</p>
              <p className="text-2xl font-bold text-gray-900">{profile.jobApplications.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <TrophyIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Overall Ranking</p>
              <p className="text-2xl font-bold text-gray-900">#{profile.overallRanking || 'Unranked'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center">
            <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
            {isEditing ? (
              <input
                type="tel"
                value={profile.phone || ''}
                onChange={(e) => updateProfile('phone', e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 focus:border-indigo-500 outline-none flex-1"
                placeholder="Phone Number"
              />
            ) : (
              <span className="text-gray-900">{profile.phone || 'Not provided'}</span>
            )}
          </div>
          <div className="flex items-center">
            <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
            {isEditing ? (
              <input
                type="url"
                value={profile.website || ''}
                onChange={(e) => updateProfile('website', e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 focus:border-indigo-500 outline-none flex-1"
                placeholder="https://your-website.com"
              />
            ) : (
              <span className="text-gray-900">{profile.website || 'Not provided'}</span>
            )}
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderSkillsTab = () => {
    if (!profile) return null;
    
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Skills & Ratings</h2>
          <div className="space-y-4">
            {profile.skillRatings.map((skill, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">{skill.skill}</span>
                    <span className="text-sm text-gray-500">{skill.endorsements} endorsements</span>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full" 
                        style={{ width: `${(skill.rating / 5) * 100}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center">
                      <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                      <span className="text-sm text-gray-600">{skill.rating}/5</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderEducationTab = () => {
    if (!profile) return null;
    
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Education</h2>
            {isEditing && (
              <button
                onClick={() => {
                  const newEducation = {
                    institution: '',
                    degree: '',
                    field: '',
                    graduationYear: new Date().getFullYear(),
                    gpa: undefined
                  };
                  updateProfile('education', [...profile.education, newEducation]);
                }}
                className="flex items-center px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Education
              </button>
            )}
          </div>
          <div className="space-y-4">
            {profile.education.map((edu, index) => (
              <div key={index} className="border-l-4 border-indigo-500 pl-4 relative">
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={edu.degree}
                        onChange={(e) => {
                          const newEducation = [...profile.education];
                          newEducation[index].degree = e.target.value;
                          updateProfile('education', newEducation);
                        }}
                        className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none flex-1"
                        placeholder="Degree (e.g., Bachelor of Science)"
                      />
                      <input
                        type="text"
                        value={edu.field}
                        onChange={(e) => {
                          const newEducation = [...profile.education];
                          newEducation[index].field = e.target.value;
                          updateProfile('education', newEducation);
                        }}
                        className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none flex-1"
                        placeholder="Field of Study"
                      />
                    </div>
                    <input
                      type="text"
                      value={edu.institution}
                      onChange={(e) => {
                        const newEducation = [...profile.education];
                        newEducation[index].institution = e.target.value;
                        updateProfile('education', newEducation);
                      }}
                      className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none w-full"
                      placeholder="Institution Name"
                    />
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={edu.graduationYear}
                        onChange={(e) => {
                          const newEducation = [...profile.education];
                          newEducation[index].graduationYear = parseInt(e.target.value);
                          updateProfile('education', newEducation);
                        }}
                        className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none w-24"
                        placeholder="Year"
                      />
                      <input
                        type="number"
                        step="0.1"
                        value={edu.gpa || ''}
                        onChange={(e) => {
                          const newEducation = [...profile.education];
                          newEducation[index].gpa = e.target.value ? parseFloat(e.target.value) : undefined;
                          updateProfile('education', newEducation);
                        }}
                        className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none w-24"
                        placeholder="GPA"
                      />
                      <button
                        onClick={() => {
                          const newEducation = profile.education.filter((_, i) => i !== index);
                          updateProfile('education', newEducation);
                        }}
                        className="text-red-600 hover:text-red-800 px-2"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{edu.degree} in {edu.field}</h3>
                    <p className="text-gray-600">{edu.institution}</p>
                    <div className="flex items-center mt-2 text-sm text-gray-500">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Graduated {edu.graduationYear}
                      {edu.gpa && <span className="ml-4">GPA: {edu.gpa}</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Certifications</h2>
            {isEditing && (
              <button
                onClick={() => {
                  const newCertification = {
                    name: '',
                    issuer: '',
                    date: new Date().toISOString().split('T')[0],
                    credentialId: ''
                  };
                  updateProfile('certifications', [...profile.certifications, newCertification]);
                }}
                className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Certification
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profile.certifications.map((cert, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                <div className="flex items-start">
                  <DocumentCheckIcon className="h-6 w-6 text-green-600 mr-3 mt-1" />
                  <div className="flex-1">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={cert.name}
                          onChange={(e) => {
                            const newCertifications = [...profile.certifications];
                            newCertifications[index].name = e.target.value;
                            updateProfile('certifications', newCertifications);
                          }}
                          className="border border-gray-300 rounded px-2 py-1 focus:border-indigo-500 outline-none w-full text-sm font-medium"
                          placeholder="Certification Name"
                        />
                        <input
                          type="text"
                          value={cert.issuer}
                          onChange={(e) => {
                            const newCertifications = [...profile.certifications];
                            newCertifications[index].issuer = e.target.value;
                            updateProfile('certifications', newCertifications);
                          }}
                          className="border border-gray-300 rounded px-2 py-1 focus:border-indigo-500 outline-none w-full text-sm"
                          placeholder="Issuing Organization"
                        />
                        <input
                          type="text"
                          value={cert.date}
                          onChange={(e) => {
                            const newCertifications = [...profile.certifications];
                            newCertifications[index].date = e.target.value;
                            updateProfile('certifications', newCertifications);
                          }}
                          className="border border-gray-300 rounded px-2 py-1 focus:border-indigo-500 outline-none w-full text-sm"
                          placeholder="Issue Date"
                        />
                        <input
                          type="text"
                          value={cert.credentialId || ''}
                          onChange={(e) => {
                            const newCertifications = [...profile.certifications];
                            newCertifications[index].credentialId = e.target.value;
                            updateProfile('certifications', newCertifications);
                          }}
                          className="border border-gray-300 rounded px-2 py-1 focus:border-indigo-500 outline-none w-full text-xs"
                          placeholder="Credential ID (optional)"
                        />
                        <button
                          onClick={() => {
                            const newCertifications = profile.certifications.filter((_, i) => i !== index);
                            updateProfile('certifications', newCertifications);
                          }}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <h3 className="font-medium text-gray-900">{cert.name}</h3>
                        <p className="text-sm text-gray-600">{cert.issuer}</p>
                        <p className="text-sm text-gray-500 mt-1">Issued {cert.date}</p>
                        {cert.credentialId && (
                          <p className="text-xs text-gray-400">ID: {cert.credentialId}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderExperienceTab = () => {
    if (!profile) return null;
    
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Work Experience</h2>
            {isEditing && (
              <button
                onClick={() => {
                  const newExperience = {
                    company: '',
                    position: '',
                    startDate: '',
                    endDate: '',
                    description: ''
                  };
                  updateProfile('experience', [...(profile.experience || []), newExperience]);
                }}
                className="flex items-center px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Experience
              </button>
            )}
          </div>
          <div className="space-y-4">
            {(profile.experience || []).map((exp, index) => (
              <div key={index} className="border-l-4 border-purple-500 pl-4 relative">
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={exp.company}
                        onChange={(e) => {
                          const newExperience = [...(profile.experience || [])];
                          newExperience[index].company = e.target.value;
                          updateProfile('experience', newExperience);
                        }}
                        className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none flex-1"
                        placeholder="Company Name"
                      />
                      <input
                        type="text"
                        value={exp.position}
                        onChange={(e) => {
                          const newExperience = [...(profile.experience || [])];
                          newExperience[index].position = e.target.value;
                          updateProfile('experience', newExperience);
                        }}
                        className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none flex-1"
                        placeholder="Position"
                      />
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={exp.startDate}
                        onChange={(e) => {
                          const newExperience = [...(profile.experience || [])];
                          newExperience[index].startDate = e.target.value;
                          updateProfile('experience', newExperience);
                        }}
                        className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none"
                        placeholder="Start Date"
                      />
                      <input
                        type="date"
                        value={exp.endDate || ''}
                        onChange={(e) => {
                          const newExperience = [...(profile.experience || [])];
                          newExperience[index].endDate = e.target.value || undefined;
                          updateProfile('experience', newExperience);
                        }}
                        className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none"
                        placeholder="End Date (leave empty if current)"
                      />
                      <button
                        onClick={() => {
                          const newExperience = (profile.experience || []).filter((_, i) => i !== index);
                          updateProfile('experience', newExperience);
                        }}
                        className="text-red-600 hover:text-red-800 px-2"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <textarea
                      value={exp.description}
                      onChange={(e) => {
                        const newExperience = [...(profile.experience || [])];
                        newExperience[index].description = e.target.value;
                        updateProfile('experience', newExperience);
                      }}
                      className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none w-full"
                      placeholder="Job description and responsibilities"
                      rows={3}
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{exp.position}</h3>
                    <p className="text-gray-600 font-medium">{exp.company}</p>
                    <div className="flex items-center mt-1 text-sm text-gray-500">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {exp.startDate} - {exp.endDate || 'Present'}
                    </div>
                    <p className="text-gray-700 mt-2">{exp.description}</p>
                  </div>
                )}
              </div>
            ))}
            {(profile.experience || []).length === 0 && !isEditing && (
              <p className="text-gray-500 text-center py-8">No work experience added yet</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'skills':
        return renderSkillsTab();
      case 'education':
        return renderEducationTab();
      case 'experience':
        return renderExperienceTab();
      case 'insights':
        return renderInsightsTab();
      default:
        return renderOverviewTab();
    }
  };

  const renderInsightsTab = () => {
    return <ProfileViewsInsights isPremium={true} />;
  };

  if (loading || !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {renderTab()}

        {/* Save/Cancel buttons when editing */}
        {isEditing && (
          <div className="fixed bottom-6 right-6 flex gap-3">
            <button
              onClick={() => setIsEditing(false)}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProfile}
              disabled={loading}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentProfile;